import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, boolean, uniqueIndex } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Import existing enums from schema-lenders.ts
import { feeTypeEnum, amountTypeEnum, feeFrequencyEnum } from "./schema-lenders.js";
// Import pricing_source_enum from F001's schema module
import { pricingSourceEnum } from "./schema-facility-pricing.js";

// ============================================================================
// Facility Fees Table
// ============================================================================

export const facilityFees = pgTable("crm_x_facility_fees", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // FK to crm_x_facilities — CASCADE on delete
  facilityId: uuid("facility_id").notNull(),

  // Fee Classification
  feeType: feeTypeEnum("fee_type").notNull(),
  feeName: varchar("fee_name", { length: 100 }).notNull(),
  amountType: amountTypeEnum("amount_type").notNull(),

  // Amount Configuration
  fixedAmount: numeric("fixed_amount", { precision: 15, scale: 2 }),
  percentageAmount: numeric("percentage_amount", { precision: 5, scale: 4 }),

  // Fee Schedule
  feeFrequency: feeFrequencyEnum("fee_frequency").notNull().default("one_time"),
  isMandatory: boolean("is_mandatory").default(false),

  description: text("description"),

  // Source tracking (pricing_source_enum from F001 migration)
  source: pricingSourceEnum("source").notNull().default("manual"),

  // FK to crm_x_lender_fees — SET NULL on delete (source fee may be deleted)
  sourceLenderFeeId: uuid("source_lender_fee_id"),

  // Audit Fields
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedBy: uuid("updated_by"),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
}, (table) => ({
  activeKeyUnique: uniqueIndex("crm_x_facility_fees_active_key_unique")
    .on(table.facilityId, table.feeType, sql`lower(${table.feeName})`)
    .where(sql`${table.deletedAt} IS NULL`),
}));

// ============================================================================
// Zod Schemas
// ============================================================================

export const insertFacilityFeeSchema = createInsertSchema(facilityFees, {
  facilityId: z.string().uuid(),
  feeType: z.enum([
    "application_fee",
    "establishment_fee",
    "valuation_fee",
    "legal_fee",
    "service_fee",
    "exit_fee",
    "variation_fee",
    "discharge_fee",
  ]),
  feeName: z.string().min(1).max(100),
  amountType: z.enum(["fixed", "percentage", "variable"]),
  fixedAmount: z.string().nullable().optional(),
  percentageAmount: z.string().nullable().optional(),
  feeFrequency: z.enum(["one_time", "monthly", "quarterly", "annual"]).optional(),
  isMandatory: z.boolean().optional(),
  description: z.string().nullable().optional(),
  source: z.enum(["manual", "product_snapshot", "repricing_event"]).optional(),
  sourceLenderFeeId: z.string().uuid().nullable().optional(),
  organisationId: z.string().uuid().nullable().optional(),
  createdBy: z.string().uuid().nullable().optional(),
  updatedBy: z.string().uuid().nullable().optional(),
});

export const selectFacilityFeeSchema = createSelectSchema(facilityFees);

export const updateFacilityFeeSchema = insertFacilityFeeSchema.partial();

// ============================================================================
// TypeScript Types
// ============================================================================

export type FacilityFee = typeof facilityFees.$inferSelect;
export type InsertFacilityFee = typeof facilityFees.$inferInsert;
